// ============================================================
//  FEATURE: Receipt Guestbook
//  A thermal-receipt-styled guestbook. Prints a faux RECEIPT OF
//  YOUR VISIT with a runtime timestamp, ascii dividers, a barcode
//  strip and a randomized surreal fortune/diagnosis. Visitors sign
//  with a name + short message; entries persist in localStorage and
//  stack as torn receipt slips.
//  Self-contained. No network. Mount standalone or inside a window
//  (programText="GUESTBOOK.RCP").
// ============================================================

import * as React from "react";
import { useState, useEffect } from "react";
import "./receiptguestbook.css";

const KEY = "ambulate_guestbook";
const DIV = "* * * * * * * * * * * * * * * *";

const FORTUNES = [
    "you have been walking in circles",
    "the eye approves",
    "your gait is irregular and beautiful",
    "symptoms: nostalgia, static",
    "a door you forgot is still open",
    "the floor remembers your weight",
    "diagnosis: chronic almost",
    "you are owed one (1) good omen",
    "the hallway extends when unobserved",
    "your reflection left early",
    "low battery in the dream sector",
    "you taste like a half-remembered hum",
];

function randomFortune() {
    return FORTUNES[Math.floor(Math.random() * FORTUNES.length)];
}

// a torn barcode strip of variable-width black bars
function Barcode() {
    const bars = [];
    for (let i = 0; i < 34; i++) {
        const w = 1 + Math.floor(Math.random() * 4);
        bars.push(
            <div
                key={i}
                className="r-bar"
                style={{ width: w + "px", opacity: i % 5 === 0 ? 0.55 : 1 }}
            />
        );
    }
    return <div className="r-barcode">{bars}</div>;
}

function ReceiptGuestbook(props) {
    const [entries, setEntries] = useState(() => {
        try {
            const raw = localStorage.getItem(KEY);
            return raw ? JSON.parse(raw) : [];
        } catch (e) {
            return [];
        }
    });

    const [name, setName] = useState("");
    const [msg, setMsg] = useState("");
    // header fortune is stable per-render-session; new one each mount
    const [headerFortune] = useState(() => randomFortune());

    useEffect(() => {
        try {
            localStorage.setItem(KEY, JSON.stringify(entries));
        } catch (e) {
            /* swallow quota / privacy-mode errors */
        }
    }, [entries]);

    function handleSubmit(e) {
        e.preventDefault();
        const cleanName = name.trim().slice(0, 20) || "ANONYMOUS";
        const cleanMsg = msg.trim().slice(0, 80);
        const entry = {
            // Date.now() alone can collide if two entries land in the same ms;
            // append a random suffix so React keys stay unique.
            id: Date.now() + "-" + Math.random().toString(36).slice(2, 8),
            name: cleanName,
            msg: cleanMsg,
            ts: new Date().toLocaleString(),
            fortune: randomFortune(),
        };
        setEntries((prev) => [...prev, entry]);
        setName("");
        setMsg("");
    }

    function handleClear() {
        try {
            localStorage.removeItem(KEY);
        } catch (e) {
            /* ignore */
        }
        setEntries([]);
    }

    return (
        <div className="receipt-shell">
            {/* ---------- HEADER / RECEIPT OF YOUR VISIT ---------- */}
            <div className="receipt-slip r-header">
                <div className="r-center r-shout">AMBULATION ZONE</div>
                <div className="r-center r-div">{DIV}</div>
                <div className="r-center">RECEIPT OF YOUR VISIT</div>
                <div className="r-center r-dim">{new Date().toLocaleString()}</div>
                <div className="r-center r-dim">VISITOR #{entries.length + 1}</div>
                <div className="r-center r-div">{DIV}</div>
                <div className="r-line">
                    DIAGNOSIS:&nbsp;<span className="r-fortune">{headerFortune}</span>
                </div>
                <Barcode />
                <div className="r-center r-div">{DIV}</div>

                {/* ---------- SIGN FORM ---------- */}
                <form className="r-form" onSubmit={handleSubmit}>
                    <label className="r-label" htmlFor="r-name-input">
                        NAME
                    </label>
                    <input
                        id="r-name-input"
                        className="r-input"
                        type="text"
                        maxLength={20}
                        placeholder="who walked through"
                        aria-label="Your name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                    />
                    <label className="r-label" htmlFor="r-msg-input">
                        MESSAGE
                    </label>
                    <textarea
                        id="r-msg-input"
                        className="r-input r-area"
                        maxLength={80}
                        placeholder="leave a mark on the tape"
                        aria-label="Your message"
                        value={msg}
                        onChange={(e) => setMsg(e.target.value)}
                    />
                    <button className="r-btn" type="submit">
                        &gt;&gt; PRINT &lt;&lt;
                    </button>
                </form>
            </div>

            {/* ---------- STACKED ENTRY SLIPS ---------- */}
            {entries
                .slice()
                .reverse()
                .map((en) => (
                    <div className="receipt-slip r-entry" key={en.id}>
                        <div className="r-name">{en.name}</div>
                        {en.msg ? <div className="r-msg">{en.msg}</div> : null}
                        <div className="r-div r-center">{DIV}</div>
                        <div className="r-ts">{en.ts}</div>
                        <div className="r-fortune r-entry-fortune">~ {en.fortune} ~</div>
                        <div className="r-div r-center">{DIV}</div>
                        <div className="r-center r-thanks">THANK YOU</div>
                    </div>
                ))}

            {entries.length > 0 ? (
                <button
                    className="r-clear"
                    type="button"
                    aria-label="Clear all guestbook entries"
                    onClick={handleClear}
                >
                    [ CLEAR ALL ]
                </button>
            ) : null}
        </div>
    );
}

export default ReceiptGuestbook;
